import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Check } from "lucide-react";
import { motion } from "framer-motion";
import { useEffect } from "react";
import projects, { getProjectById } from "../data/projects";
import Footer from "../components/Footer";

export default function ProjectDetail() {
  const { id } = useParams();
  const project = getProjectById(id);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!project) {
    return <Navigate to="/" replace />;
  }

  const currentIndex = projects.findIndex((p) => p.id === id);
  const nextProject = projects[(currentIndex + 1) % projects.length];

  return (
    <main data-testid="project-detail-page" className="grain pt-24 md:pt-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Link
          to="/#work"
          data-testid="project-back-link"
          className="inline-flex items-center gap-2 font-display text-xs uppercase tracking-[0.25em] text-zinc-400 hover:text-[#f5e6d3] transition-colors"
        >
          <ArrowLeft size={14} /> All work
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="mt-10 md:mt-14 flex items-center gap-3"
        >
          <span className="font-display text-xs uppercase tracking-[0.3em] text-[#f5e6d3]">
            Project {project.index}
          </span>
          <span className="w-10 h-px bg-[#f5e6d3]" />
          <span className="font-display text-xs uppercase tracking-[0.3em] text-zinc-500">
            {project.category}
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.05 }}
          data-testid="project-title"
          className="mt-6 font-display uppercase leading-[0.9] tracking-tighter text-5xl sm:text-7xl lg:text-[8vw]"
        >
          {project.title}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="mt-8 font-editorial italic text-xl md:text-2xl text-zinc-300 max-w-3xl leading-snug"
        >
          {project.short}
        </motion.p>
      </div>

      <motion.div
        initial={{ opacity: 0, scale: 1.02 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1 }}
        className="relative mt-12 md:mt-20 overflow-hidden"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="relative aspect-[16/10] overflow-hidden bg-zinc-900">
            <img
              src={project.cover}
              alt={project.title}
              data-testid="project-hero-image"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </motion.div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 mt-20 md:mt-32 grid md:grid-cols-12 gap-10 md:gap-16">
        <aside className="md:col-span-4 md:sticky md:top-28 md:self-start space-y-10">
          <div>
            <div className="font-display text-xs uppercase tracking-[0.25em] text-zinc-500 mb-2">
              Client
            </div>
            <div className="font-display text-lg uppercase tracking-tight">
              {project.client}
            </div>
          </div>
          <div>
            <div className="font-display text-xs uppercase tracking-[0.25em] text-zinc-500 mb-2">
              Role
            </div>
            <div className="font-display text-lg uppercase tracking-tight">
              {project.role}
            </div>
          </div>
          <div>
            <div className="font-display text-xs uppercase tracking-[0.25em] text-zinc-500 mb-2">
              Year
            </div>
            <div className="font-display text-lg uppercase tracking-tight">
              {project.year}
            </div>
          </div>
          <div>
            <div className="font-display text-xs uppercase tracking-[0.25em] text-zinc-500 mb-3">
              Stack
            </div>
            <ul className="space-y-2" data-testid="project-tech-list">
              {project.tech.map((t) => (
                <li
                  key={t}
                  className="font-body text-base text-zinc-300 flex items-center gap-3"
                >
                  <span className="w-1.5 h-1.5 bg-[#f5e6d3]" />
                  {t}
                </li>
              ))}
            </ul>
          </div>
          <a
            href={project.liveUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="project-live-link"
            className="group font-display text-xs uppercase tracking-[0.25em] bg-[#f5e6d3] text-black px-6 py-4 rounded-full hover:bg-white transition-colors inline-flex items-center gap-3"
          >
            Visit live site
            <ArrowUpRight
              size={14}
              className="group-hover:rotate-45 transition-transform"
            />
          </a>
        </aside>

        <div className="md:col-span-8 space-y-12">
          <div>
            <h2 className="font-display text-3xl md:text-4xl uppercase tracking-tight mb-6">
              About the project
            </h2>
            <p
              data-testid="project-description"
              className="font-body text-base md:text-lg text-zinc-300 leading-relaxed"
            >
              {project.description}
            </p>
          </div>

          <div>
            <h3 className="font-display text-xs uppercase tracking-[0.25em] text-[#f5e6d3] mb-5">
              Highlights
            </h3>
            <ul className="space-y-4">
              {project.highlights.map((h) => (
                <li
                  key={h}
                  className="flex items-start gap-3 font-body text-base md:text-lg text-zinc-200"
                >
                  <Check
                    size={20}
                    className="text-[#f5e6d3] shrink-0 mt-1"
                  />
                  {h}
                </li>
              ))}
            </ul>
          </div>

          <div className="space-y-8" data-testid="project-gallery">
            {/* Desktop browser frame */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7 }}
              className="rounded-lg overflow-hidden border border-white/10 bg-zinc-950 shadow-2xl"
            >
              <div className="flex items-center gap-2 px-4 py-3 bg-zinc-900 border-b border-white/10">
                <span className="w-3 h-3 rounded-full bg-red-500/70" />
                <span className="w-3 h-3 rounded-full bg-yellow-500/70" />
                <span className="w-3 h-3 rounded-full bg-green-500/70" />
                <div className="ml-4 flex-1 max-w-md mx-auto bg-black/40 rounded-full px-3 py-1 text-[11px] font-body text-zinc-500 truncate text-center">
                  {project.liveUrl.replace(/^https?:\/\//, "")}
                </div>
              </div>
              <div className="aspect-[16/10] bg-zinc-900">
                <img
                  src={project.desktop}
                  alt={`${project.title} desktop view`}
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                />
              </div>
            </motion.div>

            <div className="grid sm:grid-cols-5 gap-6 items-start">
              {/* Mobile mockup */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7 }}
                className="sm:col-span-2 mx-auto sm:mx-0 w-full max-w-[260px]"
              >
                <div className="relative aspect-[9/19] rounded-[2.5rem] border-[10px] border-zinc-800 bg-zinc-900 overflow-hidden shadow-2xl">
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-5 bg-zinc-800 rounded-b-2xl z-10" />
                  <img
                    src={project.mobile}
                    alt={`${project.title} mobile view`}
                    className="w-full h-full object-cover object-top"
                    loading="lazy"
                  />
                </div>
                <div className="mt-4 font-display text-xs uppercase tracking-[0.2em] text-zinc-500 text-center sm:text-left">
                  Mobile view
                </div>
              </motion.div>

              {/* Second desktop view (in-page scroll capture) */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, delay: 0.1 }}
                className="sm:col-span-3"
              >
                <div className="rounded-lg overflow-hidden border border-white/10 bg-zinc-950">
                  <div className="flex items-center justify-between px-4 py-2 bg-zinc-900 border-b border-white/10">
                    <span className="font-display text-[10px] uppercase tracking-[0.25em] text-[#f5e6d3]">
                      In-page section
                    </span>
                    <span className="font-display text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                      Desktop · 1440
                    </span>
                  </div>
                  <div className="relative aspect-[4/3] bg-zinc-900">
                    <img
                      src={project.desktopAlt}
                      alt={`${project.title} secondary view`}
                      className="w-full h-full object-cover object-top"
                      loading="lazy"
                    />
                  </div>
                </div>
                <a
                  href={project.liveUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="project-gallery-live-link"
                  className="mt-4 inline-flex items-center gap-2 font-display text-xs uppercase tracking-[0.2em] text-zinc-400 hover:text-[#f5e6d3] transition-colors"
                >
                  See it live <ArrowUpRight size={12} />
                </a>
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 mt-24 md:mt-40 border-t border-white/10 pt-12">
        <div className="flex items-end justify-between flex-wrap gap-6">
          <div>
            <div className="font-display text-xs uppercase tracking-[0.25em] text-zinc-500 mb-3">
              Next project
            </div>
            <Link
              to={`/project/${nextProject.id}`}
              data-testid="project-next-link"
              className="group font-display text-3xl md:text-5xl uppercase tracking-tight hover:text-[#f5e6d3] transition-colors inline-flex items-center gap-4"
            >
              {nextProject.title}
              <ArrowUpRight
                size={28}
                className="group-hover:rotate-45 transition-transform"
              />
            </Link>
          </div>
          <Link
            to="/#work"
            className="font-display text-xs uppercase tracking-[0.25em] border border-white/20 px-7 py-4 rounded-full hover:border-white hover:bg-white/5 transition-colors inline-flex items-center gap-2"
          >
            <ArrowLeft size={14} /> All work
          </Link>
        </div>
      </div>

      <Footer />
    </main>
  );
}
